import { useCallback, useEffect, useState } from "react";
import { EyeIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Modal,
  Page,
  PageHeader,
  PageLoader,
  Panel,
  SearchInput,
  TableShell,
  Td,
  Textarea,
  Th,
  Tr,
} from "../components/ui";
import {
  closeEcuTicket,
  connectEcuWebhook,
  testEcuWebhook,
  downloadEcuTicketAttachment,
  getEcuTicket,
  listEcuTickets,
  replyEcuTicket,
  ticketErrorMessage,
} from "../utils/ecuTicketsApi";

const STATUS_TONE = { OPEN: "info", WAITING: "warning", CLOSED: "neutral" };
const STATUS_LABEL = { OPEN: "Open", WAITING: "Answered", CLOSED: "Closed" };

const formatWhen = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/**
 * Automatic support tickets — requests to the Automatic support team about the service (a new
 * solution, a bug, a tuning or a special request), with the original / modified file attached.
 * Requests are opened from the Automatic page, for the file uploaded there; this page lists them
 * and holds their threads. Customers see their own; admins (isAdmin) see every customer's.
 */
const EcuTickets = ({ isAdmin = false }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openId, setOpenId] = useState(null);
  const [webhookNotice, setWebhookNotice] = useState(null);
  const [webhookBusy, setWebhookBusy] = useState(false);

  // Admin: connect / test the Automatic support reply webhook (customers are notified of replies through it).
  const runWebhook = async (action) => {
    setWebhookBusy(true);
    setWebhookNotice(null);
    try {
      if (action === "connect") {
        const saved = await connectEcuWebhook();
        setWebhookNotice({ tone: "success", text: `Connected — replies will be delivered to ${saved?.url}.` });
      } else {
        const result = await testEcuWebhook();
        setWebhookNotice(
          result?.delivered
            ? { tone: "success", text: `Test ping delivered (HTTP ${result.status}).` }
            : { tone: "error", text: `Test ping failed: ${result?.error || "no response"}.` }
        );
      }
    } catch (err) {
      setWebhookNotice({ tone: "error", text: await ticketErrorMessage(err, "Webhook request failed.") });
    } finally {
      setWebhookBusy(false);
    }
  };

  const reload = useCallback(async () => {
    try {
      setTickets(await listEcuTickets());
      setError("");
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to load tickets."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const term = searchTerm.trim().toLowerCase();
  const filtered = tickets.filter(
    (t) =>
      !term ||
      (t.subject || "").toLowerCase().includes(term) ||
      (t.brand || "").toLowerCase().includes(term) ||
      (t.ecu || "").toLowerCase().includes(term) ||
      (t.vdpUsername || "").toLowerCase().includes(term)
  );

  if (loading) {
    return (
      <Page>
        <PageHeader title="Automatic Support" onBack={() => window.history.back()} />
        <PageLoader label="Loading tickets…" />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Automatic Support"
        subtitle={`${tickets.length} ticket${tickets.length === 1 ? "" : "s"} with the Automatic support team`}
        onBack={() => window.history.back()}
      >
        {!isAdmin ? (
          <Button to="/automatic" variant="ghost">
            Open a request from Automatic
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={webhookBusy}
              onClick={() => runWebhook("connect")}
              title="Connect the reply webhook to this server, so customers are notified of replies"
            >
              Connect webhook
            </Button>
            <Button variant="ghost" size="sm" disabled={webhookBusy} onClick={() => runWebhook("test")}>
              Send test
            </Button>
          </>
        )}
      </PageHeader>

      {webhookNotice ? (
        <Alert tone={webhookNotice.tone} onClose={() => setWebhookNotice(null)}>
          {webhookNotice.text}
        </Alert>
      ) : null}

      <Alert tone="error" onClose={() => setError("")}>
        {error}
      </Alert>

      <SearchInput
        placeholder={isAdmin ? "Search subject, brand, ECU, customer…" : "Search subject, brand, ECU…"}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filtered.length === 0 ? (
        <EmptyState
          title="No tickets"
          hint={
            tickets.length === 0
              ? isAdmin
                ? "No customer has opened an Automatic support ticket yet."
                : "Upload a file on the Automatic page, then use Support request to ask for a new solution, report a bug, or request a tuning."
              : "No ticket matches your search."
          }
        />
      ) : (
        <Panel padded={false}>
          <TableShell className="rounded-[22px] border-0">
            <thead>
              <tr>
                <Th>Subject</Th>
                {isAdmin ? <Th>Customer</Th> : null}
                <Th>Status</Th>
                <Th>Opened</Th>
                <Th>Last activity</Th>
                <Th align="center">View</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ticket) => (
                <Tr key={ticket.id}>
                  <Td className="font-medium text-gray-800 dark:text-slate-200">
                    <span className="inline-flex items-center gap-2">
                      {ticket.customerUnread && !isAdmin ? (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" title="New reply" />
                      ) : null}
                      {ticket.subject}
                    </span>
                  </Td>
                  {isAdmin ? <Td className="text-xs">{ticket.vdpUsername}</Td> : null}
                  <Td>
                    <Badge tone={STATUS_TONE[ticket.status] || "neutral"}>
                      {STATUS_LABEL[ticket.status] || ticket.status}
                    </Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">{formatWhen(ticket.createdAt)}</Td>
                  <Td className="whitespace-nowrap text-xs">{formatWhen(ticket.updatedAt)}</Td>
                  <Td className="text-center">
                    <button
                      type="button"
                      title="View ticket"
                      onClick={() => setOpenId(ticket.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </Panel>
      )}

      {openId ? (
        <TicketThreadModal
          ticketId={openId}
          isAdmin={isAdmin}
          onClose={() => setOpenId(null)}
          onChanged={reload}
        />
      ) : null}
    </Page>
  );
};

/** One ticket: the thread, its files, a reply box, and Close. */
const TicketThreadModal = ({ ticketId, isAdmin, onClose, onChanged }) => {
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setTicket(await getEcuTicket(ticketId));
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to load the ticket."));
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    setError("");
    try {
      setTicket(await replyEcuTicket(ticketId, reply.trim()));
      setReply("");
      onChanged();
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to send the reply."));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    setBusy(true);
    setError("");
    try {
      setTicket(await closeEcuTicket(ticketId));
      onChanged();
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to close the ticket."));
    } finally {
      setBusy(false);
    }
  };

  const download = async (att) => {
    try {
      await downloadEcuTicketAttachment(ticketId, att.id, att.fileName);
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to download the file."));
    }
  };

  const closed = ticket?.status === "CLOSED";

  return (
    <Modal title={ticket ? ticket.subject : "Ticket"} onClose={onClose} size="max-w-3xl" tone="plain">
      {!ticket ? (
        error ? <Alert tone="error">{error}</Alert> : <PageLoader label="Loading ticket…" />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
            <Badge tone={STATUS_TONE[ticket.status] || "neutral"}>
              {STATUS_LABEL[ticket.status] || ticket.status}
            </Badge>
            {ticket.brand ? <span>{ticket.brand} · {ticket.ecu}</span> : null}
            <span>opened {formatWhen(ticket.createdAt)}</span>
          </div>

          {ticket.selectedItems && ticket.selectedItems.length > 0 ? (
            <p className="text-sm text-gray-600 dark:text-slate-300">
              <span className="text-gray-400">Requested:</span> {ticket.selectedItems.join(", ")}
            </p>
          ) : null}

          {ticket.attachments && ticket.attachments.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {ticket.attachments.map((att) => (
                <button
                  key={att.id}
                  type="button"
                  onClick={() => download(att)}
                  className="inline-flex items-center gap-1.5 rounded-[10px] border border-gray-300 px-2.5 py-1 text-xs text-gray-700 transition hover:border-red-400 dark:border-[#3a3a3d] dark:text-slate-200"
                >
                  <PaperClipIcon className="h-3.5 w-3.5" />
                  <span className="text-gray-400">
                    {att.kind === "ORIGINAL" ? "ORI" : att.kind === "MODIFIED" ? "MOD" : "FILE"}:
                  </span>
                  <span className="max-w-[200px] truncate">{att.fileName}</span>
                  <span className="text-gray-400">({formatBytes(att.sizeBytes)})</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="max-h-[45vh] space-y-3 overflow-y-auto pr-1">
            {(ticket.messages || []).map((m) => (
              <div key={m.id} className={`flex ${m.fromAdmin ? "justify-start" : "justify-end"}`}>
                <div
                  className={`max-w-[80%] rounded-[14px] border px-3.5 py-2.5 ${
                    m.fromAdmin
                      ? "border-sky-500/20 bg-sky-500/10"
                      : "border-red-500/20 bg-red-500/5"
                  }`}
                >
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.08em]">
                    <span className={m.fromAdmin ? "text-sky-600 dark:text-sky-300" : "text-red-600 dark:text-orange-300"}>
                      {m.fromAdmin ? "Automatic support" : isAdmin ? "Customer" : "You"}
                    </span>
                    <span className="font-normal normal-case tracking-normal text-gray-400">
                      {formatWhen(m.createdAt)}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-slate-100">
                    {m.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Alert tone="error" onClose={() => setError("")}>
            {error}
          </Alert>

          {closed ? (
            <p className="text-sm text-gray-500 dark:text-slate-400">
              This ticket is closed. Open a new request if you need more help.
            </p>
          ) : (
            <div className="space-y-2">
              <Textarea
                rows={3}
                placeholder="Write a reply…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={close} disabled={busy}>
                  Close ticket
                </Button>
                <Button onClick={send} disabled={busy || !reply.trim()}>
                  {busy ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default EcuTickets;
